// src/pages/employee/Attendance.js
import React, { useState, useEffect, useMemo } from "react";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import moment from "moment";
import {
  getAttendance,
  getAllEmployee,
  addAttendance,
} from "../../services/services";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";

const Attendance = () => {
  const [activeTab, setActiveTab] = useState("today");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  // Today tab states
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [todayLoading, setTodayLoading] = useState(true);

  // Monthly Attendance states
  const [attendanceData, setAttendanceData] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [attendanceError, setAttendanceError] = useState(null);

  // Employees list states
  const [employees, setEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(true);

  const now = moment();
  const todayStr = now.format("YYYY-MM-DD");
  const currentMonth = now.format("MMMM YYYY");
  const year = now.year();
  const monthIndex = now.month();
  const daysInMonth = now.daysInMonth();

  // Responsive handling
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch today's attendance
  useEffect(() => {
    if (activeTab !== "today") return;

    const fetchToday = async () => {
      setTodayLoading(true);
      try {
        const res = await getAttendance({ date: todayStr });
        const data = res.data?.response || res.data?.data || [];
        setTodayAttendance(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load today's attendance");
        setTodayAttendance([]);
      } finally {
        setTodayLoading(false);
      }
    };

    fetchToday();
  }, [activeTab, todayStr]);

  // Fetch monthly attendance
  useEffect(() => {
    if (activeTab !== "monthly") return;

    const fetchMonthly = async () => {
      setAttendanceLoading(true);
      setAttendanceError(null);
      try {
        const payload = { year, month: monthIndex + 1 };
        const res = await getAttendance(payload);

        let data = res.data?.response || res.data?.data || res.data || [];
        if (!Array.isArray(data))
          data = data.employees || data.attendance || [];

        setAttendanceData(data);
      } catch (err) {
        console.error("Monthly fetch error:", err);
        setAttendanceError("Failed to load monthly attendance. Try again.");
        setAttendanceData([]);
      } finally {
        setAttendanceLoading(false);
      }
    };

    fetchMonthly();
  }, [activeTab, year, monthIndex]);

  // Fetch employees list
  useEffect(() => {
    if (activeTab !== "employees") return;

    const fetchEmployees = async () => {
      setEmpLoading(true);
      try {
        const res = await getAllEmployee({ page: 1, limit: 100 }); // adjust limit if needed
        if (res.data?.status) {
          setEmployees(res.data.response?.employees || []);
        } else {
          setEmployees([]);
          toast.error("Failed to load employees");
        }
      } catch (err) {
        toast.error("Failed to load employees");
        setEmployees([]);
      } finally {
        setEmpLoading(false);
      }
    };

    fetchEmployees();
  }, [activeTab]);

  const handleMarkAttendance = async (employeeId, type) => {
    try {
      const payload = {
        employee: employeeId,
        date: moment().format("YYYY-MM-DD"),
      };
      if (activeTab === "today") {
        const res = await getAttendance({ date: todayStr });
        setTodayAttendance(res.data?.response || []);
      }
      if (type === "in") {
        payload.punchIn = new Date();
      } else {
        payload.punchOut = new Date();
      }

      const res = await addAttendance(payload);

      if (res.data?.status) {
        toast.success(res.data.message);
      } else {
        toast.error("Attendance failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Attendance error");
    }
  };

  // Monthly summary calculations

  const monthlySummary = useMemo(() => {
    const map = {};

    attendanceData.forEach((rec) => {
      const empId = rec.employee?._id;
      if (!empId) return;

      if (!map[empId]) {
        map[empId] = {
          name: rec.employee.name,
          present: 0,
          absent: 0,
          holidays: 0,
          leaves: 0,
        };
      }

      if (rec.status === "Present") map[empId].present += 1;
      if (rec.status === "Leave") map[empId].leaves += 1;
      if (rec.status === "Holiday") map[empId].holidays += 1;
    });

    // calculate absent
    Object.values(map).forEach((emp) => {
      emp.absent = daysInMonth - emp.present - emp.holidays - emp.leaves;
    });

    return Object.values(map);
  }, [attendanceData, daysInMonth]);

  const totalPresent = monthlySummary.reduce(
    (sum, e) => sum + (e.present || 0),
    0,
  );
  const totalEmployeesCount = monthlySummary.length;
  const totalPossible = totalEmployeesCount * daysInMonth;
  const totalAbsent = totalPossible - totalPresent;
  const avgAttendance =
    totalPossible > 0 ? Math.round((totalPresent / totalPossible) * 100) : 0;

  // ── TODAY TAB COLUMNS ──
  const todayColumns = useMemo(
    () => [
      {
        name: "Employee",
        selector: (row) => row.employee?.name || "—",
        sortable: true,
        minWidth: "180px",
      },
      {
        name: "Punch In",
        center: true,
        selector: (row) => {
          const firstSession = row.sessions?.[0];
          return firstSession?.punchIn
            ? moment(firstSession.punchIn).format("hh:mm A")
            : "—";
        },
        minWidth: "130px",
      },
      {
        name: "Punch Out",
        center: true,
        selector: (row) => {
          const firstSession = row.sessions?.[0];
          return firstSession?.punchOut
            ? moment(firstSession.punchOut).format("hh:mm A")
            : "—";
        },
        minWidth: "130px",
      },
      {
        name: "Worked Hours",
        center: true,
        minWidth: "140px",
        cell: (row) => {
          const hoursDecimal = Number(row.workedHours) || 0;
          if (hoursDecimal <= 0) return "—";

          const totalSeconds = Math.round(hoursDecimal * 3600);
          const h = Math.floor(totalSeconds / 3600);
          const m = Math.floor((totalSeconds % 3600) / 60);
          const s = totalSeconds % 60;

          const parts = [];
          if (h > 0) parts.push(`${h}h`);
          if (m > 0 || h > 0) parts.push(`${m}m`);
          if (s > 0) parts.push(`${s}s`);

          return parts.join(" ") || "0m";
        },
      },
      {
        name: "Status",
        center: true,
        cell: (row) => {
          if (row.status === "Present") {
            const hasOut = row.sessions?.[0]?.punchOut;
            return hasOut ? (
              <span className="badge bg-success">Completed</span>
            ) : (
              <span className="badge bg-warning text-dark">Working</span>
            );
          }
          return <span className="badge bg-secondary">{row.status}</span>;
        },
        minWidth: "110px",
      },
    ],
    [],
  );

  // ── TABS CONFIG ──
  const tabs = [
    { key: "today", icon: "bi-calendar-check-fill", label: "Today" },
    { key: "monthly", icon: "bi-calendar-month-fill", label: "Monthly" },
    { key: "employees", icon: "bi-people-fill", label: "Employees" },
  ];

  // ── SUMMARY CARD COMPONENT ── (unchanged)
  const SummaryCard = ({ title, value, icon, color }) => (
    <div className={`card border-0 shadow-sm bg-${color}-light h-100`}>
      <div className="card-body d-flex align-items-center p-3">
        <div
          className={`rounded-circle bg-${color} text-white d-flex align-items-center justify-content-center me-3`}
          style={{ width: 40, height: 40 }}
        >
          <i className={`bi ${icon} fs-4`}></i>
        </div>
        <div>
          <small className="text-muted d-block">{title}</small>
          <h5 className="mb-0 fw-bold">{value}</h5>
        </div>
      </div>
    </div>
  );

  return (
    <div id="main">
      <style>{`
        .nav-link { border: none; border-bottom: 3px solid transparent; padding: 0.9rem 1.6rem; font-weight: 500; }
        .nav-link.active { color: #0d6efd; border-bottom-color: #0d6efd; }
        .bg-success-light { background-color: rgba(25,135,84,0.08) !important; }
        .bg-danger-light   { background-color: rgba(220,53,69,0.08) !important; }
        .bg-warning-light  { background-color: rgba(255,193,7,0.08) !important; }
        .bg-info-light     { background-color: rgba(13,202,240,0.08) !important; }
      `}</style>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h5 className="fw-bold mb-0">Employee Attendance</h5>
      </div>

      {/* Monthly Summary Cards */}
      {activeTab === "monthly" && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <SummaryCard
              title="Total Present"
              value={totalPresent}
              icon="bi-check-circle-fill"
              color="success"
            />
          </div>
          <div className="col-6 col-md-3">
            <SummaryCard
              title="Total Absent"
              value={totalAbsent}
              icon="bi-x-circle-fill"
              color="danger"
            />
          </div>
          <div className="col-6 col-md-3">
            <SummaryCard
              title="Attendance %"
              value={`${avgAttendance}%`}
              icon="bi-graph-up"
              color="info"
            />
          </div>
          <div className="col-6 col-md-3">
            <SummaryCard
              title="Employees"
              value={totalEmployeesCount}
              icon="bi-people-fill"
              color="primary"
            />
          </div>
        </div>
      )}

      {/* Tabs & Content */}
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white">
          <ul className="nav nav-tabs border-0 mb-0">
            {tabs.map((tab) => (
              <li className="nav-item" key={tab.key}>
                <button
                  className={`nav-link ${
                    activeTab === tab.key ? "active" : ""
                  }`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <i className={`bi ${tab.icon} me-1 d-none d-md-inline`}></i>
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="card-body p-0">
          {activeTab === "today" ? (
            <>
              <div className="p-3 border-bottom bg-light d-flex justify-content-between align-items-center">
                <p className="mb-0  text-muted">
                  <i className="bi bi-calendar-check me-2"></i>
                  Today — {now.format("ddd, DD MMM, YYYY")}
                </p>
                <small className="text-muted">Live attendance updates</small>
              </div>

              {todayLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                  <p className="mt-3 text-muted">Loading today's records...</p>
                </div>
              ) : todayAttendance.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-emoji-neutral fs-1 mb-3 d-block"></i>
                  <h5>No attendance recorded yet today</h5>
                  <small>Records appear when employees punch in/out</small>
                </div>
              ) : (
                <DataTable
                  columns={todayColumns}
                  data={todayAttendance}
                  highlightOnHover
                  striped
                  responsive
                  progressPending={todayLoading}
                  noHeader
                />
              )}
            </>
          ) : activeTab === "monthly" ? (
            // Your original monthly view (kept almost unchanged)
            <>
              <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                <h5 className="mb-0 fw-bold text-muted">{currentMonth}</h5>
              </div>

              {attendanceLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                  <p className="mt-3">Loading monthly records...</p>
                </div>
              ) : attendanceError ? (
                <div className="alert alert-danger text-center py-4 mx-3">
                  {attendanceError}
                </div>
              ) : attendanceData.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  No attendance records found for {currentMonth}
                </div>
              ) : !isMobile ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-4">Employee</th>
                        <th className="text-center">Days</th>
                        <th className="text-center">Present</th>
                        <th className="text-center">Absent</th>
                        <th className="text-center">Holidays</th>
                        <th className="text-center">Leaves</th>
                        <th className="text-center">Attendance %</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>

                    <tbody>
                      {monthlySummary.map((emp, i) => (
                        <tr key={i}>
                          <td className="ps-4">{emp.name}</td>
                          <td className="text-center">{daysInMonth}</td>
                          <td className="text-center text-success">
                            {emp.present}
                          </td>
                          <td className="text-center text-danger">
                            {emp.absent}
                          </td>
                          <td className="text-center">{emp.holidays}</td>
                          <td className="text-center">{emp.leaves}</td>
                          <td className="text-center">
                            {Math.round((emp.present / daysInMonth) * 100)}%
                          </td>
                          <td className="text-center">
                            {emp.present > daysInMonth * 0.7 ? (
                              <span className="badge bg-success">Good</span>
                            ) : emp.present > daysInMonth * 0.4 ? (
                              <span className="badge bg-warning">Average</span>
                            ) : (
                              <span className="badge bg-danger">Low</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-3">
                  {monthlySummary.map((emp, i) => (
                    <div className="card border mb-3" key={i}>
                      <div className="card-header bg-white d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0 fw-bold">
                            {emp.name || "Unknown"}
                          </h6>
                          <small className="text-muted">{emp.id || "—"}</small>
                        </div>
                        <span
                          className={`badge bg-${
                            (emp.present / daysInMonth) * 100 > 70
                              ? "success"
                              : (emp.present / daysInMonth) * 100 > 40
                                ? "warning"
                                : "danger"
                          }`}
                        >
                          {daysInMonth > 0
                            ? Math.round((emp.present / daysInMonth) * 100)
                            : 0}
                          %
                        </span>
                      </div>
                      <div className="card-body">
                        <div className="row g-2 text-center">
                          <div className="col-6">
                            <div className="border rounded p-2">
                              <small className="text-muted">Present</small>
                              <br />
                              <strong className="text-success">
                                {emp.present || 0}
                              </strong>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="border rounded p-2">
                              <small className="text-muted">Absent</small>
                              <br />
                              <strong className="text-danger">
                                {emp.absent || 0}
                              </strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : activeTab === "employees" ? (
            <div className="p-3">
              {empLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                  <p className="mt-3">Loading employees...</p>
                </div>
              ) : employees.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-people fs-1 mb-3 d-block"></i>
                  <h5>No employees found</h5>
                  <small>Add employees to see them here</small>
                </div>
              ) : (
                <DataTable
                  columns={[
                    {
                      name: "Name",
                      selector: (row) => row.name,
                      sortable: true,
                    },
                    {
                      name: "Mobile",
                      selector: (row) => row.mobile || "—",
                      minWidth: "130px",
                    },
                    {
                      name: "Email",
                      selector: (row) => row.email || "—",
                      minWidth: "180px",
                    },
                    {
                      name: "Department",
                      selector: (row) => row.department || "—",
                      minWidth: "140px",
                    },
                    {
                      name: "Role",
                      selector: (row) => row.role || "—",
                      minWidth: "130px",
                    },
                    {
                      name: "Mark",
                      center: true,
                      cell: (row) => (
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleMarkAttendance(row._id, "in")}
                          >
                            Punch In
                          </button>

                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleMarkAttendance(row._id, "out")}
                          >
                            Punch Out
                          </button>
                        </div>
                      ),
                      minWidth: "200px",
                    },
                    {
                      name: "Join Date",
                      selector: (row) =>
                        row.joinDate
                          ? moment(row.joinDate).format("DD MMM YYYY")
                          : "—",
                      sortable: true,
                      center: true,
                    },
                    {
                      name: "Actions",
                      center: true,
                      cell: (row) => (
                        <Link to={`/employee/edit/${row._id}`}>
                          <button className="btn btn-sm btn-outline-primary">
                            <i className="bi bi-pencil"></i>
                          </button>
                        </Link>
                      ),
                      ignoreRowClick: true,
                      button: true,
                    },
                  ]}
                  data={employees}
                  highlightOnHover
                  striped
                  responsive
                  noHeader
                />
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div className="d-flex justify-content-between text-muted small mt-4 pt-3 border-top">
        <div>
          Showing{" "}
          {activeTab === "today"
            ? todayAttendance.length
            : activeTab === "monthly"
              ? attendanceData.length
              : employees.length}{" "}
          records
        </div>
      </div>
    </div>
  );
};

export default headerLayoutWrapper(Attendance);
